// Types de l'API d'Ekera utilisés par l'application.
// - Ceux qui sont décrits dans l'OpenAPI v0 sont repris du fichier généré (schema.ts).
// - Les réponses de /scenarios et /constats ne sont pas décrites dans l'OpenAPI v0 (« unknown ») : leurs types
//   ci-dessous sont écrits d'après les réponses réelles de l'API et vérifiés par les tests de bout en bout.
import type { components, paths } from './schema';

type S = components['schemas'];

export type Carte = S['Carte'];
export type Objet = NonNullable<Carte['objets']>[number];
export type Lien = S['Lien'];
export type TypeObjet = S['TypeObjet'];
export type TypeLien = S['TypeLien'];
export type Provenance = S['Provenance'];
// (le nom du schéma dépend de l'OpenAPI : deux modèles « Citation » existent côté API ; on le tire de la provenance)
export type Citation = NonNullable<S['Provenance']['citations']>[number];
export type Contradiction = S['Contradiction'];
export type Correction = S['Correction'];
export type Operation = S['Operation'];
export type Changement = S['Changement'];
export type ReponseEcriture = S['ReponseEcriture'];
export type EntreeJournal = S['EntreeJournal'];
export type Evenement = S['Evenement'];
export type Preuve = S['Preuve'];
export type Sante = S['Sante'];
export type Moi = S['Moi'];
export type RoleEkera = Moi['roles'][number];

// RM2 : corps issus de l'OpenAPI ; réponses décrites d'après routes_retours.py (_vue et liste),
// car leur schéma généré vaut encore « unknown ». Aucun champ interne de décision n'est repris.
export type DepotRetour = paths['/v0/retours']['post']['requestBody']['content']['application/json'];
export type ValidationRetour = paths['/v0/retours/{retour}/validation']['post']['requestBody']['content']['application/json'];
export type PropositionRetour = NonNullable<paths['/v0/retours/{retour}/propositions']['post']['requestBody']>['content']['application/json'];
export type EtatRetour = 'recu' | 'proposition' | 'anonymisation_validee' | 'retenu' | 'ecarte' | 'livre' | 'retire';
export interface ResumeRetour {
  id: string;
  cree_le: string;
  canal: DepotRetour['canal'];
  type_objet: NonNullable<DepotRetour['type_objet']>;
  etat: EtatRetour;
  tete: string;
  envoi_disponible: false;
}
export interface EvenementRetour {
  numero: number;
  nature: EtatRetour;
  instant: string;
  classe?: string;
  mode?: 'regles' | 'modele' | 'auteur';
  raison?: string;
  texte_ekera?: string;
  version_publique?: string;
  empreinte_texte?: string;
  contenu?: { reformulation?: string; version_anonymisee?: string };
}
export interface DetailRetour extends Omit<ResumeRetour, 'etat'> {
  contenu: DepotRetour;
  evenements: EvenementRetour[];
  avertissement_validation: string;
}
export interface ListeRetours {
  retours: ResumeRetour[];
  suivant: string | null;
  envoi_disponible: false;
}

// Circuit de validation (CV-3)
export type AlertesCircuit = S['AlertesCircuit'];
export type AlerteCircuit = S['AlerteCircuit'];
export type HistoriqueCircuit = S['HistoriqueCircuit'];
export type LigneHistorique = S['LigneHistorique'];
export type EtatCircuitLivrable = S['EtatCircuitLivrable'];

// Sources lues par Ekera (docs/PIVOT-API.md § 8.11). Aucun secret : seulement la RÉFÉRENCE du secret dans le coffre.
export type SourceVue = S['SourceVue'];
export type SourceDetail = S['SourceDetail'];
export type EtatSource = SourceVue['etat'];
export type LectureVue = S['LectureVue'];
export type ErreurVue = S['ErreurVue'];
export type EssaiVue = S['EssaiVue'];
export type ResultatTestVue = S['ResultatTestVue'];
export type ContradictionSource = S['ContradictionVue'];
export type EntreeHistorique = S['EntreeHistorique'];
export type DeclarationSource = S['DeclarationSource'];

// Collecteurs sur site (docs/securite/CONCEPTION-DEPOTS-SIGNES.md § 7 à 10). L'API ne rend ni clé publique, ni kid, ni
// empreinte pour une demande en attente (O63) : ces types n'en portent donc jamais la valeur à l'écran.
export type CollecteurVue = S['CollecteurVue'];
export type CollecteurDetail = S['CollecteurDetail'];
export type EtatCollecteur = CollecteurVue['etat'];
export type EntreeJournalCollecteur = S['EntreeJournalVue'];
export type SourceLieeVue = S['SourceLieeVue'];
export type RevocationVue = S['RevocationVue'];
export type RaisonRevocation = S['DemandeRevocation']['raison'];

// Chaîne documentaire (étape 2) : livrables pré-remplis depuis les gabarits du client, licences inutilisées. Aucun texte
// n'y est rédigé par un modèle : faits cités (vérifiés mot pour mot), chiffres du moteur, raisons écrites par le code.
export type ResumeLivrable = S['ResumeLivrable'];
export type VueLivrable = S['VueLivrable'];
export type SectionVue = S['SectionVue'];
export type ChampVue = S['ChampVue'];
export type CitationVue = S['CitationVue'];
export type ACompleterVue = S['ACompleterVue'];
export type ResultatPublication = S['ResultatPublication'];
export type VueLicences = S['VueLicences'];

// Dépôt des documents dans l'application (offre Diagnostic) : type reconnu au contenu par l'API, pièces chiffrées.
export type VueDepots = S['VueDepots'];
export type PieceDeposee = S['PieceDeposee'];
export type EtatLecturePieces = S['EtatLecture'];
// Entretien en ligne : questions sur ce que les pièces n'établissent pas, réponses citées « entretien ».
// EffetVue conserve euros et rayon en « number | null » : inconnu n'est pas un effet nul.
export type EtatEntretien = S['EtatEntretien'];
export type QuestionEntretien = S['QuestionVue'];
export type QuestionDeleguee = S['QuestionDeleguee'];
export type ReponseDelegation = S['ReponseDelegation'];
export type ResultatReponseEntretien = S['ResultatReponse'];
// Rapport de diagnostic produit par l'API (PDF gardé, chiffres du moteur, citations revérifiées).
export type ResumeRapport = S['ResumeRapport'];
export type NatureDepot = NonNullable<NonNullable<paths['/v0/cartes/{cid}/depots']['post']['parameters']['query']>['nature']>;
export type ConstatLicenceVue = S['ConstatLicenceVue'];

/** Recette d'export d'un logiciel sans connecteur (catalogue). */
export interface RecetteExport {
  titre: string;
  etapes: string[];
  formats: string[];
  ne_pas_inclure: string[];
}
/** Un logiciel du catalogue (GET /v0/catalogue-sources ; l'OpenAPI le décrit comme un objet libre). */
export interface LogicielCatalogue {
  id: string;
  nom: string;
  mode: 'connecteur' | 'export';
  connecteur: string | null;
  recette_export: RecetteExport | null;
  famille: string;
  lecture: { methode: string; droits_minimaux: string };
  apporte?: string;
}
export interface CoffreCatalogue {
  schema: string;
  nom: string;
  forme: string;
  exemple: string;
}
export interface CatalogueSources {
  version: string;
  logiciels: LogicielCatalogue[];
  coffres: CoffreCatalogue[];
  seuils: { frequence_lecture_h?: number; retard_facteur?: number; [k: string]: unknown };
}
export type Acces = S['Acces'];
export type ResumeCarte = S['ResumeCarte'];
export type Montant = S['Montant'];
export type Fourchette = S['Fourchette'];
export type ObjetDecision = S['ObjetDecision'];
export type AttributsDecision = S['AttributsDecision'];
export type OptionDecision = S['Option'];
export type Raison = S['Raison'];
export type PoidsRaison = S['PoidsRaison'];
export type StatutPartage = S['StatutPartage'];
export type RetourExperience = S['RetourExperience'];
export type ObjetTravaux = S['ObjetTravaux'];
// Démonstration T4 et T5 (API 0.7.0) : pièces lues pour produire la carte, variantes de travaux recalculées par le moteur.
export type PieceLue = S['PieceLue'];
export type ManqueCalcul = S['ManqueCalcul'];
export type Variantes = S['Variantes'];
export type Variante = S['Variante'];
export type IndicateursVariante = S['IndicateursVariante'];
// Essai d'une variante en lecture seule (API 0.11.0) : POST /v0/cartes/{id}/variantes/essai, rien n'est écrit.
export type DemandeEssai = S['DemandeEssai'];
// Du besoin à l'application (étape 2) : application cible proposée et son effet calculé par le moteur (lecture seule).
export type ApplicationCible = S['ApplicationCible'];
export type EffetApplication = S['EffetApplication'];
export type EssaiVariante = S['EssaiVariante'];
export type CalculEssai = S['CalculEssai'];
export type AttributsTravaux = S['AttributsTravaux'];
export type ObjetConstat = S['ObjetConstat'];
export type Gravite = S['Gravite'];
export type ErreurApiCorps = S['Erreur'];
export type PlanEcriture = S['PlanEcriture'];
// Circuit de validation (CV3I-1) : la version exacte d'un livrable soumise, gardée dans l'instance.
export type ContenuSoumisVue = S['ContenuSoumisVue'];
export type Validation = S['Validation'];
export type DemandeValidation = S['DemandeValidation'];
export type ReponseValidation = S['ReponseValidation'];
export type DemandePlan = S['DemandePlan'];
export type ReponsePlan = S['ReponsePlan'];
export type ReponseDefi = S['ReponseDefi'];
export type AssertionWebAuthn = S['AssertionWebAuthn'];
export type CreationWebAuthn = S['CreationWebAuthn'];
export type EtatWebAuthn = S['EtatWebAuthn'];
export type OptionsCreation = S['OptionsCreation'];
export type OptionsAssertion = S['OptionsAssertion'];
export type CleEnrolee = S['CleEnrolee'];
export type EtatCle = S['EtatCle'];
export type VueCle = S['VueCle'];
export type VueCleAdmin = S['VueCleAdmin'];
export type EtatAdresseAlerte = S['EtatAdresseAlerte'];
export type VueAdresse = S['VueAdresse'];
export type VueAdresseAdmin = S['VueAdresseAdmin'];
export type AdresseDeclaree = S['AdresseDeclaree'];
export type EnvoiCode = S['EnvoiCode'];
export type EtatAdresse = VueAdresse['etat'];

export type VueAnssi =
  paths['/v0/cartes/{cid}/exports/anssi/{vue}']['get']['parameters']['path']['vue'];

/** Montant tel que le moteur le rend (devise toujours EUR). */
export interface MontantEur {
  valeur: number;
  devise: 'EUR';
}

export interface FourchetteEur {
  bas: MontantEur;
  haut: MontantEur;
}

/** Postes du détail de perte d'un scénario (une valeur basse, une valeur haute). */
export interface DetailPerte {
  marge_perdue: MontantEur;
  cout_rattrapage: MontantEur;
  penalites: MontantEur;
  reconstitution_donnees: MontantEur;
  reponse_incident: MontantEur;
  financement: MontantEur;
  tresorerie_differee: MontantEur;
  total: MontantEur;
}

export interface EtapeAttaque {
  motif: string;
  composants: string[];
}

export interface Scenario {
  id: string;
  nature: 'attaque' | 'panne';
  entree: string;
  motif: string;
  vraisemblance?: number;
  composants_compromis?: string[];
  composants_arretes: string[];
  processus_arretes: string[];
  rayon: number;
  restaurable?: boolean;
  etapes?: EtapeAttaque[];
  frequence_annuelle: [number, number];
  perte: FourchetteEur;
  detail_perte?: [DetailPerte, DetailPerte];
}

export interface MesureNis2 {
  mesure: string;
  id: string;
  libelle: string;
  statut: 'conforme' | 'partiel' | 'ecart' | string;
  constats: string[];
}

export interface Indicateurs {
  rayon_max: number;
  rayon_moyen: number;
  risque: number;
  portes_entree: number;
  restaurable: boolean;
  sauvegarde_hors_ligne: boolean;
  points_uniques_defaillance: string[];
  score_nis2: number;
  mesures_nis2_conformes: number;
}

export interface Hypotheses {
  restoreDays: [number, number];
  rebuildDays: [number, number];
  outageDays: [number, number];
  responseCost: [number, number];
  frequency: Record<string, [number, number]>;
  outageFrequency: [number, number];
}

/** Réponse de GET /v0/cartes/{id}/scenarios (moteur déterministe). */
export interface Calcul {
  manques?: ManqueCalcul[];
  fournisseurs?: FournisseurCritique[];
  indicateurs: Indicateurs;
  scenarios: Scenario[];
  nis2: MesureNis2[];
  references_cassees: string[];
  perte_annuelle_attendue?: FourchetteEur;
  methode: string;
  hypotheses: Hypotheses;
}

/** CX6 : scénarios conditionnels ; aucune fréquence annuelle supposée pour un tiers. */
export interface ScenarioFournisseur {
  etat: 'acces_connus' | 'acces_a_confirmer';
  entrees: string[];
  processus_arretes: string[];
  composants_arretes: string[];
  perte: [number, number] | null;
}
export interface FournisseurCritique {
  id: string;
  nom: string;
  critique: boolean;
  scenario: ScenarioFournisseur;
  limite: string;
  acces: { id: string; label: string; kind: string; enabled: boolean;
    provenance: { citations: { source_id: string; locator: string; excerpt: string }[] } }[];
  donnees_confiees: { id: string; label: string }[];
  pieces: { source_id: string; kind: string; reference_connue: boolean }[];
  mesures: { id: string; libelle: string; detail: string; cout: { total: number; horizon: number };
    apres: ScenarioFournisseur; continuite_a_verifier: string[] }[];
}

/** Réponse de GET /v0/cartes/{id}/constats. */
export interface ReponseConstats {
  manques?: ManqueCalcul[];
  constats: ObjetConstat[];
  liens: Lien[];
}

/** Objet ou lien avec son ETag, lu juste avant d'écrire. */
export interface ElementLu<T> {
  element: T;
  etag: string;
}

// ---------------------------------------------------------------- console d'interrogation (écran « Interroger »)
// `POST /v0/cartes/{cid}/questions` (OpenAPI v0, schéma `Reponse`). L'API sérialise toujours toutes les listes, mais
// l'OpenAPI les déclare facultatives (valeur par défaut) : `normaliserReponse` les fixe pour que l'écran n'ait pas à
// s'en soucier.

/** Un morceau d'énoncé : six formes, texte brut (jamais de HTML ni de Markdown). */
export type MorceauConsole = S['MTexte'] | S['MObjet'] | S['MAttribut'] | S['MLien'] | S['MChiffre'] | S['MValeur'];
export type ChiffreConsole = S['Chiffre'];
export type OrigineChiffre = ChiffreConsole['origine'];
export type SourceConsole = S['Source'];
export type CitationConsole = NonNullable<SourceConsole['citations']>[number];
export type EnonceConsole = S['Enonce'];
export type ManqueConsole = S['Manque'];
export type CodeManque = ManqueConsole['code'];
export type OperationConsole = S['OperationFaite'];
export type ElementPhrase = S['ElementE'] | S['ElementT'];
export type PhraseConsole = S['Phrase'];
export type LigneSyntheseConsole = S['LigneSynthese'];
export type RaisonRefus = NonNullable<S['Reponse']['refus']>['raison'];

type ListesReponse = 'phrases' | 'synthese' | 'chiffres' | 'sources' | 'operations' | 'manques';
export type ReponseConsole = Omit<S['Reponse'], ListesReponse> & {
  phrases: PhraseConsole[];
  synthese: LigneSyntheseConsole[];
  chiffres: ChiffreConsole[];
  sources: SourceConsole[];
  operations: OperationConsole[];
  manques: ManqueConsole[];
};

/** Corps de la question : 500 caractères au plus ; « regles » : questions types seulement, sans modèle. */
export type CorpsQuestion = paths['/v0/cartes/{cid}/questions']['post']['requestBody']['content']['application/json'];

export function normaliserReponse(r: S['Reponse']): ReponseConsole {
  return {
    ...r,
    phrases: r.phrases ?? [],
    synthese: r.synthese ?? [],
    chiffres: r.chiffres ?? [],
    sources: r.sources ?? [],
    operations: r.operations ?? [],
    manques: r.manques ?? [],
  };
}
